"use client";

import { Check } from "lucide-react";
import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceBenefitItem,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

const cadiImage = "/images/services/svc-cloud-architecture.jpg";

// Cancels the layout container padding so band sections read full-width.
const bandBleed = "-mx-4 sm:-mx-6 lg:-mx-8";

const cloudServiceRows = [
  {
    title: "Infrastructure as Code (IaC)",
    description:
      "Every environment is declared, versioned and reproducible — no more hand-built servers or configuration drift.",
    features: [
      "Terraform for multi-cloud infrastructure provisioning",
      "AWS CloudFormation for AWS-specific deployments",
      "Azure Resource Manager templates",
      "Version-controlled infrastructure configurations",
    ],
  },
  {
    title: "CI/CD Pipeline Automation",
    description:
      "Ship faster with automated pipelines that test, build and deploy every change reliably.",
    features: [
      "Jenkins pipeline automation",
      "GitLab CI/CD integration",
      "GitHub Actions workflows",
      "Automated testing and deployment",
      "Container orchestration with Kubernetes",
    ],
  },
  {
    title: "Cloud Security & Compliance",
    description:
      "Security and compliance designed into the architecture from day one, not bolted on after.",
    features: [
      "Identity and Access Management (IAM)",
      "Network security architecture",
      "Encryption at rest and in transit",
      "Security compliance automation",
      "Regular security audits and monitoring",
    ],
  },
];

const cadiFAQs = [
  {
    question: "How much does cloud architecture design cost in Malaysia?",
    answer:
      "A focused cloud architecture engagement for a single workload typically starts from RM 30,000-80,000, while enterprise-scale design and migration programmes range from RM 100,000 to RM 500,000+. Anchor Sprint scopes work in phases, so you can start with a well-architected foundation and expand as your needs grow.",
  },
  {
    question: "How long does a cloud migration or infrastructure build take?",
    answer:
      "A pilot environment or single-application migration is usually live in 4-8 weeks. Full infrastructure builds and multi-application migrations typically take 3-6 months. Because we work with Infrastructure as Code, every stage is repeatable and progress is visible from the first sprint.",
  },
  {
    question: "Should my Malaysian business choose AWS, Azure, or Google Cloud?",
    answer:
      "It depends on your existing stack, compliance needs, and team skills. AWS offers the broadest service catalogue, Azure integrates tightly with Microsoft 365 and enterprise identity, and Google Cloud excels at data analytics and Kubernetes. Anchor Sprint is platform-neutral — we design for the cloud that fits your business, including multi-cloud architectures with Terraform.",
  },
  {
    question: "How do you reduce the risk of migrating to the cloud?",
    answer:
      "We migrate in stages: infrastructure is defined as code and tested in parallel environments before any cutover, with rollback plans at every step. Critical workloads run side by side with existing systems until they are proven stable, so your business never depends on a big-bang switchover.",
  },
  {
    question: "Do you provide support after the cloud infrastructure goes live?",
    answer:
      "Yes. Anchor Sprint offers ongoing managed support covering monitoring, security patching, cost optimization, and architecture reviews. Many Malaysian clients keep us on a monthly retainer so their infrastructure keeps improving after launch.",
  },
];

export default function CloudArchitecture() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const translatedFaqs = cadiFAQs.map((faq) => ({
    question: translate(faq.question),
    answer: translate(faq.answer),
  }));

  return (
    <ServicePageLayout
      title="Cloud Architecture Design and Implementation in Malaysia"
      breadcrumb="Services / Cloud Architecture"
      currentServiceId="cadi"
      accent="Cloud Architecture"
      dek={
        "Infrastructure as Code, CI/CD automation and cloud security — engineered for scale on AWS, Azure and Google Cloud."
      }
    >
      <ServiceHeroImage
        src={cadiImage}
        alt="Cloud Architecture and DevOps Solutions Malaysia"
      />

      <div className="mb-16">
        <h2 className="mb-6 font-display text-3xl font-bold text-neutral-emphasis">
          {translate(
            "Cloud infrastructure that scales with your business, not against it",
          )}
        </h2>
        <p className="max-w-2xl leading-relaxed text-neutral-muted">
          {translate(
            "Anchor Sprint specializes in cloud architecture design and implementation for Malaysian businesses — robust, secure infrastructure built with Infrastructure as Code and modern DevOps practices, so your platform scales without surprise costs or downtime.",
          )}
        </p>
      </div>

      <ServiceSection
        title="Our Cloud Architecture Services"
        titleLevel="h2"
        eyebrow="What we build"
        accent="Cloud Architecture"
        dek="Three disciplines, one goal — infrastructure your team can deploy, secure and scale with confidence."
        band="ivory"
        className={bandBleed}
      >
        <div className="divide-y divide-border border-y border-border">
          {cloudServiceRows.map((row) => (
            <div
              key={row.title}
              className="grid gap-6 py-10 lg:grid-cols-2 lg:gap-16"
            >
              <div className="max-w-prose">
                <h3 className="mb-3 font-display text-2xl font-semibold text-neutral-emphasis">
                  {translate(row.title)}
                </h3>
                <p className="leading-relaxed text-neutral-muted">
                  {translate(row.description)}
                </p>
              </div>
              <ul className="space-y-3">
                {row.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 text-neutral-muted"
                  >
                    <Check
                      className="mt-1 h-4 w-4 flex-shrink-0 text-primary"
                      aria-hidden
                    />
                    <span>{translate(feature)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ServiceSection>

      <ServiceSection
        title="Cloud Platforms We Support"
        titleLevel="h2"
        eyebrow="Platforms"
        accent="Cloud Platforms"
        dek="Certified expertise across the three major clouds, and the judgment to recommend the right one for your workload."
        band="surface"
        className={bandBleed}
      >
        <ServiceGrid columns={3}>
          <ServiceFeatureCard
            icon="fab fa-aws"
            title="Amazon Web Services"
            description="Comprehensive AWS cloud solutions with expertise in EC2, S3, RDS, and serverless architectures."
          />

          <ServiceFeatureCard
            icon="fab fa-microsoft"
            title="Microsoft Azure"
            description="Azure cloud services including AKS, Azure Functions, and Azure DevOps integration."
          />

          <ServiceFeatureCard
            icon="fab fa-google"
            title="Google Cloud"
            description="GCP solutions with expertise in GKE, Cloud Functions, and BigQuery."
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="Our DevOps Methodology"
        titleLevel="h2"
        eyebrow="How we work"
        accent="DevOps Methodology"
        dek="A delivery rhythm that automates the path from commit to production."
        band="ivory"
        className={bandBleed}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ServiceBenefitItem
            title="Continuous Integration"
            description="Automated code integration and testing for faster development cycles."
            icon="fas fa-check-circle"
          />

          <ServiceBenefitItem
            title="Continuous Deployment"
            description="Automated deployment pipelines for reliable software delivery."
            icon="fas fa-check-circle"
          />

          <ServiceBenefitItem
            title="Infrastructure Automation"
            description="Automated infrastructure provisioning and configuration management."
            icon="fas fa-check-circle"
          />

          <ServiceBenefitItem
            title="Monitoring & Optimization"
            description="Comprehensive monitoring and continuous optimization of cloud resources."
            icon="fas fa-check-circle"
          />
        </div>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        titleLevel="h2"
        eyebrow="FAQ"
        band="surface"
        className={bandBleed}
      >
        <ServiceFaq items={cadiFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={translatedFaqs} />

      <ConsultationCTA
        title="Ready to modernise your cloud infrastructure?"
        description="Let's discuss how our cloud architecture expertise can help your business achieve scalability and efficiency."
      />
    </ServicePageLayout>
  );
}
